import { useState, useEffect } from "react";
import axios from "axios";

const DEFAULT_BIASED_RADIUS = 5;
const RADIUS_IN_KM = 1000;

const useGoogleMapsApi = ({
    setAddress = () => null,
    setForcePickup = () => null,
    setSuggestions = () => null,
    setHouseNumberError = () => null,
    inputId,
    config, // ReactConfig object
    biasedRadius = DEFAULT_BIASED_RADIUS,
}) => {
    const [loading, setLoading] = useState(false);

    const initGoogleMaps = () => {
        window.pottsalatGoogleSuggestToken =
            new google.maps.places.AutocompleteSessionToken();
        if (window.pottsalatGoogleInit) return;
        window.pottsalatGoogleInit = true;
        window.pottsalatAutocompleteService =
            new google.maps.places.AutocompleteService();
        window.pottsalatGeocoderService = new google.maps.Geocoder();
        window.pottsalatPlacesService = new google.maps.places.PlacesService(
            document.createElement("div")
        );
    };

    useEffect(() => {
        if (window.google) {
            initGoogleMaps();
            return;
        }
        let googleScript = document.createElement("script");
        googleScript.onload = initGoogleMaps;
        googleScript.src =
            "https://maps.googleapis.com/maps/api/js?key=" +
            reactConfig.googleMapsKey +
            "&libraries=places";
        document.head.appendChild(googleScript);
    }, []);

    const formatSuggestionText = (suggestionText) =>
        suggestionText.replace(", Germany", "").replace(", Deutschland", "");

    const fetchGoogleMapsSuggestRun = async (
        value,
        postcodeLocation = null
    ) => {
        if (!value) {
            props.setAddressSuggestions([]);
            return [];
        }

        setLoading(true);
        let params = {
            input: value,
            sessionToken: window.googleSuggestToken,
            types: ["address"],
            componentRestrictions: {
                country: "de",
            },
        };

        if (
            postcodeLocation !== null &&
            postcodeLocation.lat !== null &&
            postcodeLocation.lon !== null
        ) {
            const [lat, lng] = [
                parseFloat(postcodeLocation.lat),
                parseFloat(postcodeLocation.lon),
            ];

            const storeLocation = new google.maps.LatLng(lat, lng);
            params.location = storeLocation;
            params.origin = storeLocation;
            params.radius = biasedRadius * RADIUS_IN_KM;
        }

        return await window.pottsalatAutocompleteService.getPlacePredictions(
            params,
            (data) => {
                setLoading(false);

                if (!data) {
                    setSuggestions([]);
                    return;
                }

                const suggestions = data.map((item) => {
                    return {
                        id: item.place_id,
                        text: formatSuggestionText(item.description),
                    };
                });

                setSuggestions(suggestions);
            }
        );
    };

    const handleHouseNumberCheck = (hasHouseNumber) => {
        setHouseNumberError(!hasHouseNumber);

        if (inputId && !hasHouseNumber) {
            document.getElementById(inputId).focus();
        }
    };

    const checkPlaceDetails = (googleId) => {
        let params = {
            placeId: googleId,
            sessionToken: window.pottsalatGoogleSuggestToken,
            fields: ["address_components", "geometry"],
        };

        window.pottsalatPlacesService.getDetails(params, (data) => {
            const hasHouseNumber = data.address_components.some((component) =>
                component.types.includes("street_number")
            );

            handleHouseNumberCheck(hasHouseNumber);
        });
    };

    const selectAddressSuggestion = (googleId) => {
        let params = {
            placeId: googleId,
            sessionToken: window.pottsalatGoogleSuggestToken,
            fields: ["address_components", "geometry"],
        };

        window.pottsalatPlacesService.getDetails(params, (data) => {
            let address = { address: "", housenumber: "" };
            for (const component of data.address_components) {
                if (component.types.includes("street_number"))
                    address.housenumber = component.long_name;
                else if (component.types.includes("route"))
                    address.address = component.long_name;
                else if (component.types.includes("postal_code"))
                    address.postcode = component.long_name;
                else if (component.types.includes("locality"))
                    address.locality = component.long_name;
            }

            window.pottsalatGoogleSuggestToken =
                new google.maps.places.AutocompleteSessionToken();

            const hasHouseNumber = Boolean(address.housenumber);
            handleHouseNumberCheck(hasHouseNumber);

            if (!hasHouseNumber) {
                return;
            }

            axios
                .get(config?.url + "/geocode", {
                    params: {
                        lat: data.geometry.location.lat(),
                        lon: data.geometry.location.lng(),
                        address: address.address + " " + address.housenumber,
                        postcode: address.postcode,
                        city: address.locality,
                    },
                })
                .then((response) => {
                    if (response.status !== 200 || !response.data) {
                        setForcePickup(true);
                        return;
                    }

                    setAddress({
                        address,
                        area: response.data,
                        location: {
                            lat: data.geometry.location.lat(),
                            lon: data.geometry.location.lng(),
                        },
                    });
                })
                .catch(() => {
                    setForcePickup(true);
                    setAddress(null);
                });
        });
    };

    return {
        loading,
        fetchSuggestedLocations: fetchGoogleMapsSuggestRun,
        selectAddressSuggestion,
        checkPlaceDetails,
    };
};

export default useGoogleMapsApi;
